import { Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, FormControl, FormLabel, Flex, InputGroup, InputLeftElement, Button, Image, Input, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, Spinner, Center, Select, Box, Text, DrawerCloseButton } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import UploadLogo from "../../../../assets/images/UploadLogo.svg";
import { baseUrl } from "../../../../hooks/useFetch";
import { CustomTable, CustomToast } from "../../../../components";
import { Column } from "../../../../components/CustomTable/customTable";
import { hiringCategoryList, IWorkModeDropdown } from "./modal";
import { useFetch } from "../../../../hooks/useFetch";
import { useAuth } from "../../../../hooks/useAuth";
import { DownloadIcon } from "@chakra-ui/icons";

interface BulkExcellUploadProps {
    excellModalOpen: boolean;
    setExcellUploadModalOpen: (isOpen: boolean) => void;
    jobId: number;
    loadCandidateList: () => void;
}

export default function BulkExcellUpload({ excellModalOpen, setExcellUploadModalOpen, jobId, loadCandidateList }: BulkExcellUploadProps) {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);
    const { loginObj } = useAuth();
    const [excell, setExcell] = useState<File | null>(null);
    const [candidates, setCandidates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [workModeList, setWorkModeList] = useState<IWorkModeDropdown[]>([]);
    const [globalWorkMode, setGlobalWorkMode] = useState("");
    const [globalHiringCategory, setGlobalHiringCategory] = useState("");


    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = "/sample/BulkExcellUpload.xlsx";
        link.download = 'bulkUploadExcell.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileChange = async (e: any) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            const allowedExcelTypes = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
                "application/vnd.ms-excel", // .xls
            ];

            if (!allowedExcelTypes.includes(file.type)) {
                addToast({ message: "Only Excel files (.xlsx or .xls) are allowed.", status: "error", });
                return;
            }
            setExcell(file);
        }
    };

    const handleUpload = async () => {
        if (!excell) {
            addToast({ message: "Please select a file to upload.", status: "error", });
            return;
        }

        const allowedExcelTypes = [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
            "application/vnd.ms-excel", // .xls
        ];

        if (!allowedExcelTypes.includes(excell.type)) {
            addToast({ message: "Only Excel files (.xlsx or .xls) are allowed.", status: "error", });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("file", excell);

        try {
            const response = await fetch(`${baseUrl}Candidate/bulkExcellUpload`, {
                method: "POST",
                body: formData,
            });
            if (response.ok) {
                const data = await response.json();
                setCandidates(data);
                setExcellUploadModalOpen(false);
                setExcell(null);
                setDrawerOpen(true);
            } else {
                const error = await response.json()
                addToast({ message: error?.message, status: "error" })
                setExcell(null);
            }
        } catch (error) {
            console.error("Excel upload failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleResumeChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type !== "application/pdf") {
                addToast({ message: "Only PDF files are allowed.", status: "warning" });
                return;
            }

            // ✅ Check file size (5 MB limit)
            if (file.size > 5 * 1024 * 1024) {
                addToast({ message: "Resume file must be less than 5 MB.", status: "warning" });
                return;
            }
            setCandidates((prev) => {
                const updated = [...prev];
                updated[index].resumeFile = file;
                return updated;
            });
        }
    };

    const handleSaveAll = async () => {
        const missingResume = candidates.filter(c => !c.resumeFile).map(c => c.name || "Unnamed Candidate");
        const missingWorkMode = candidates.filter(c => !c.work_Mode).map(c => c.name || "Unnamed Candidate");

        if (missingWorkMode.length > 0) {
            addToast({ message: `Please select work mode for: ${missingWorkMode.join(", ")}`, status: "warning", });
            return;
        }

        if (missingResume.length > 0) {
            addToast({ message: `Please upload resume for: ${missingResume.join(", ")}`, status: "warning", });
            return;
        }


        const formData = new FormData();
        const candidateMeta = candidates.map(c => ({
            name: c.name || "",
            email: c.email || "",
            phone: c.phone || "",
            current_Location: c.address || "",
            experience_In_Months: c.experience || "",
            current_Salary: c.current_Salary || "",
            expected_Salary: c.expected_Salary || "",
            notice_Period: c.notice_Period || "",
            work_Mode: c.work_Mode || "",
            hiring_Category: c.hiring_Category || "",   
        }));

        formData.append("CandidatesJson", JSON.stringify(candidateMeta));
        formData.append("JobId", jobId.toString());

        candidates.forEach((candidate, index) => {
            if (candidate.resumeFile) {
                formData.append(`Resumes_${candidate.email}`, candidate.resumeFile);
            }
        });

        try {
            setLoading(true);
            const response = await fetch(`${baseUrl}Candidate/BulkExcellRecruitment`, {
                method: "POST",
                headers: { Authorization: `Bearer ${loginObj?.token || ''}` },
                body: formData,
            });

            if (response.ok) {
                addToast({ message: "All candidates saved successfully!", status: "success" });
                setDrawerOpen(false);
                setCandidates([]);
                loadCandidateList();
            } else {
                const err = await response.text();
                addToast({ message: "Failed to save candidates: " + err, status: "error" });
            }
        } catch (error) {
            console.error("Save candidates error:", error);
            addToast({ message: "Something went wrong while saving.", status: "error" });
        } finally {
            setLoading(false);
        }
    };

    const loadWorkMode = async () => {
        const response = await fetchApi("RecruitmentMaster/WorkMood", "GET", "", "", "");
        if (response) {
            setWorkModeList(response);
        }
    }

    useEffect(() => {
        loadWorkMode();
    }, [])

    return (
        <>
            {/* 📦 Upload Excel Modal */}
            <Modal isOpen={excellModalOpen} onClose={() => setExcellUploadModalOpen(false)} isCentered size="xl" >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader><Text align={"center"}>Upload Excel File</Text></ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <input type="file" multiple accept=".xls,.xlsx" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
                        <Box border="2px dashed #8F8F8F" borderRadius="12px" p={8} w="100%" textAlign="center" cursor="pointer" onClick={() => fileInputRef?.current?.click()}>
                            <Image src={UploadLogo} mx="auto" mb={3} w="50px" />
                            <Text className="font-poppins text_md text_medium" mb={2}>Click to select excel</Text>
                            <Text fontSize="sm" color="gray.500">(Only excel  file)</Text>
                        </Box>
                        <Flex  mb={4} mt={4}>
                            <Button rightIcon={<DownloadIcon />} size="sm" colorScheme="blue" variant="outline"  onClick={handleDownloadTemplate}>
                                Download Sample File
                            </Button>
                        </Flex>
                        <Center>
                            <Button colorScheme="green" onClick={handleUpload} isDisabled={!excell || loading} >
                                {loading ? <Spinner size="sm" /> : "Upload & Preview"}
                            </Button>
                        </Center>
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* 📋 Drawer showing parsed Excel data */}
            <Drawer isOpen={drawerOpen} placement="right" onClose={() => setDrawerOpen(false)}>
                <DrawerOverlay />
                <DrawerContent maxWidth="85%">
                    <DrawerCloseButton />
                    <DrawerHeader>Candidate Details from Excel</DrawerHeader>
                    <DrawerBody>
                        <CustomTable value={candidates} loading={loading} emptyMessage="No Candidate Found." >
                            <Column header="Name" field="name" />
                            <Column header="Email" field="email" />
                            <Column header="Phone" field="phone" />
                            <Column header="Current Location" field="address" />
                            <Column
                                header={
                                    <Flex direction="column" >
                                        <Text className='font-poppins text_medium text_sm' mb={2}>Work Mode</Text>
                                        <Select
                                            placeholder="Apply to all"
                                            w="150px"
                                            height="28px"
                                            fontSize="xs"
                                            value={globalWorkMode}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setGlobalWorkMode(value);

                                                setCandidates(prev => prev.map(c => ({
                                                    ...c,
                                                    work_Mode: value
                                                })));
                                            }}
                                        >
                                            {workModeList.map((mode) => (
                                                <option key={mode.id} value={mode.id}>
                                                    {mode.work_Mood}
                                                </option>
                                            ))}
                                        </Select>
                                    </Flex>
                                }
                                body={(_, index) => (
                                    <Select
                                        placeholder="Select"
                                        w="150px"
                                        height="30px"
                                        fontSize="sm"
                                        value={candidates[index]?.work_Mode || ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCandidates(prev => {
                                                const updated = [...prev];
                                                updated[index].work_Mode = value;
                                                return updated;
                                            });
                                        }}
                                    >
                                        {workModeList.map((mode) => (
                                            <option key={mode.id} value={mode.id}>
                                                {mode.work_Mood}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />

                            <Column
                                header={
                                    <Flex direction="column">
                                        <Text className='font-poppins text_medium text_sm' mb={1}>Hiring Category</Text>

                                        <Select
                                            placeholder="Apply to all"
                                            w="150px"
                                            height="28px"
                                            fontSize="xs"
                                            value={globalHiringCategory}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setGlobalHiringCategory(value);

                                                // Apply globally
                                                setCandidates(prev =>
                                                    prev.map(item => ({ ...item, hiring_Category: value }))
                                                );
                                            }}
                                        >
                                            {hiringCategoryList.map((cat) => (
                                                <option key={cat.id} value={cat.id}>
                                                    {cat.category}
                                                </option>
                                            ))}
                                        </Select>
                                    </Flex>
                                }
                                body={(_, index) => (
                                    <Select
                                        placeholder="Select"
                                        w="150px"
                                        height="30px"
                                        fontSize="sm"
                                        value={candidates[index]?.hiring_Category || ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setCandidates(prev => {
                                                const updated = [...prev];
                                                updated[index].hiring_Category = value;
                                                return updated;
                                            });
                                        }}
                                    >
                                        {hiringCategoryList.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.category}
                                            </option>
                                        ))}
                                    </Select>
                                )}
                            />

                            <Column header="Resume" body={(_, index) => (
                                <input type="file" accept=".pdf" onChange={(e) => handleResumeChange(index, e)} />
                            )}
                            />
                            <Column header="Experience" field="experience" />
                            <Column header="Current Salary" field="current_Salary" />
                            <Column header="Expected Salary" field="expected_Salary" />
                            <Column header="Notice Period" field="notice_Period" />
                        </CustomTable>

                        {/* Save button aligned right */}
                        <Flex justify="flex-end" mt={6} w={"100%"} position={"absolute"} bottom={0} right={0} mb={4} mr={4}>
                            <Button colorScheme="green" onClick={handleSaveAll} isDisabled={candidates.length === 0 || loading}>
                                {loading ? <Spinner size="sm" /> : "Save All Candidates"}
                            </Button>
                        </Flex>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
}
